import React from "react";
import {
  Text,
  Stack,
  HStack,
  Tooltip,
  Icon,
  Progress,
  Flex,
  IconButton,
} from "@chakra-ui/react";
import {
  primary,
  textSecondary,
  textPrimary,
  newBorder,
  white,
  backgroundLight,
  color8,
  primaryAlt,
  whiteAlt,
} from "../../../../utils/colors";
import dayjs from "dayjs";
import { TbDiscount, TbMailX, TbMessage, TbMessage2Off } from "react-icons/tb";
import useNumberFormatter from "../../../../hooks/useNumberFormatter";
import StatusButtonNewLiteTp from "../../../../components/buttons/StatusButtonNewLiteTp";
import MoreButtonLite from "../../../../components/buttons/MoreButtonLite";
import { FaEnvelope } from "react-icons/fa6";
import { MdLocalHospital } from "react-icons/md";

export const columnsDesktop = (
  translate,
  navigate,
  deleteTreatmentPlanHandler,
  handleSortChange,
  slug,
  setShowDeleteConfirmation,
  setPlanId,
  openEditSidePanel,
  setTreatmentPlanID,
  setPatientName,
  currencySymbol
) => [
  {
    Header: () => (
      <Flex onClick={() => handleSortChange("treatmentPlanID")} h="100%">
        <Flex align="center" cursor="pointer">
          <Text mr={2}>{translate("patient.columnID")}</Text>
        </Flex>
      </Flex>
    ),
    accessor: "treatmentPlanID",
    disableSortBy: true,
    width: 100,
    Cell: ({ row }) => {
      const { treatmentPlanID } = row.original;

      return (
        <Text
          textAlign="left"
          overflow="hidden"
          whiteSpace="nowrap"
          fontSize="xs"
          color={textSecondary}
          fontWeight="500"
        >
          {treatmentPlanID}
        </Text>
      );
    },
  },
  {
    Header: () => (
      <Flex onClick={() => handleSortChange("createdAt")} h="100%">
        <Flex align="center" cursor="pointer">
          <Text mr={2}>{translate("treatmentPlan.columnCreated")}</Text>
        </Flex>
      </Flex>
    ),
    accessor: "createdAt",
    width: 120,
    disableSortBy: true,
    Cell: ({ row }) => {
      const { createdAt } = row.original;

      // Get the formatted date
      const createdAtDate = dayjs(createdAt).format("MMMM DD");

      // Get the current year and the year of the createdAt date
      const createdYear = dayjs(createdAt).year();
      const currentYear = dayjs().year();

      return (
        <Stack gap={0}>
          <Text
            overflow="hidden"
            whiteSpace="nowrap"
            fontSize="sm"
            fontWeight="500"
            color={textPrimary}
            textTransform="capitalize"
            textAlign="left"
          >
            {createdAtDate}
          </Text>
          {createdYear !== currentYear && ( // Only show year if it's not the current year
            <Text
              fontSize="xs"
              color={textSecondary}
              fontWeight="500"
              textAlign="left"
              mt="3px"
            >
              {createdYear}
            </Text>
          )}
        </Stack>
      );
    },
  },
  {
    Header: () => (
      <Flex onClick={() => handleSortChange("patient.lastName")} h="100%">
        <Flex align="center" cursor="pointer">
          <Text mr={2}>{translate("treatmentPlan.columnPatient")}</Text>
        </Flex>
      </Flex>
    ),
    accessor: "patient",
    disableSortBy: true,
    Cell: ({ row }) => {
      const { patient } = row.original;

      const patientID = patient && patient.patientID;
      // get birthdate year  only from the date
      const birthDate = patient && patient.birthDate;

      // const birthDate = patient && patient.birthDate;

      // const age = dayjs().year() - dayjs(birthDate).year();

      return (
        <Stack gap={1} pr={0} borderRight={`0px solid ${newBorder}`}>
          <HStack gap={2}>
            <Text
              fontSize="md"
              color={textPrimary}
              fontWeight="600"
              isTruncated // This will add ellipsis
              whiteSpace="nowrap"
              w="100%"
              maxWidth="200px"
              textAlign="left"
              lineHeight="1.2"
            >
              {patient && patient.lastName}
              {/* {firstName} */}
              <Text as="span" fontWeight="400">
                {" "}
                {/* {lastName} */}
                {patient && patient.firstName}
              </Text>
            </Text>
          </HStack>
          <Flex align="center">
            <Flex align="center">
              {/* <Circle size="18px" bg={white} color={textSecondary} mr={0.5}>
                <Icon as={TbFingerprint} fontSize="0.75rem" />
              </Circle> */}
              <Text fontSize="xs" color={textSecondary} fontWeight="500">
                {patientID}
              </Text>
              <Text
                fontSize="xs"
                color={textSecondary}
                fontWeight="200"
                mx={1.5}
              >
                |
              </Text>

              <Text fontSize="xs" color={textSecondary} fontWeight="500">
                {dayjs(birthDate).format("YYYY")}
              </Text>
            </Flex>
          </Flex>
        </Stack>
      );
    },
  },

  {
    Header: () => (
      <Flex onClick={() => handleSortChange("totalPrice")} h="100%">
        <Flex align="center" cursor="pointer">
          <Text mr={2}>{translate("patient.columnServices")}</Text>
        </Flex>
      </Flex>
    ),
    accessor: "totalPrice",
    width: 190,
    disableSortBy: true,
    Cell: ({ row }) => {
      const {
        servicesCompleted,
        servicesNotCompleted,
        totalPrice,
        hasDiscount,
        isReimbursement,
      } = row.original;

      const formatNumber = useNumberFormatter(); // Get the formatting function
      const formattedPrice = formatNumber(totalPrice); // Call the function with totalPrice

      const totalServices = servicesCompleted + servicesNotCompleted;

      return (
        <Flex
          align="center"
          justify="flex--start"
          maxWidth="180px"
          minW="180px"
        >
          <Flex
            align="center"
            justify="flex-start"
            borderLeft={`1px solid ${newBorder}`}
            borderRight={`1px solid ${newBorder}`}
            px={6}
            w="100%"
          >
            {/* <Icon as={TbMessageCircle} fontSize="lg" color="#989EA7" /> */}
            <Stack gap={0} textAlign="left" mr={0}>
              <Text fontSize="sm" color={textSecondary} fontWeight="500">
                <Text as="span" color={textPrimary} fontWeight="500">
                  {servicesCompleted}
                </Text>
                /{totalServices}
              </Text>
              <Progress
                className="attendeesTotal"
                w="40px"
                value={(servicesCompleted / totalServices) * 100}
                mt="0.25rem"
                color={primary}
                size="sm"
                height="3px"
                borderRadius="0.5rem"
              />
            </Stack>

            <Flex align="center" ml={4}>
              {hasDiscount && (
                <Flex align="center" justify="center" textAlign="center" mr={0}>
                  <Icon
                    as={TbDiscount}
                    color={primaryAlt}
                    fontSize="0.875rem"
                    mr="0.125rem"
                    position="relative"
                    top="-1px"
                  />
                </Flex>
              )}

              {isReimbursement && (
                <Flex align="center" justify="center" textAlign="center" mr={0}>
                  <Icon
                    as={MdLocalHospital}
                    color={primaryAlt}
                    fontSize="0.875rem"
                    mr="0.125rem"
                    position="relative"
                    top="-1px"
                  />
                </Flex>
              )}

              <Text
                fontSize="sm"
                color={textSecondary}
                fontWeight="500"
                whiteSpace="nowrap"
                textAlign="right"
              >
                {currencySymbol} {formattedPrice}
              </Text>
            </Flex>
          </Flex>
        </Flex>
      );
    },
  },

  {
    Header: () => (
      <Flex onClick={() => handleSortChange("notes")} h="100%">
        <Flex align="center" cursor="pointer">
          <Text mr={2}>{translate("treatmentPlan.columnNotes")}</Text>
        </Flex>
      </Flex>
    ),
    accessor: "notes",
    width: 100,
    disableSortBy: true,
    Cell: ({ row }) => {
      const { notes } = row.original;

      // console.log("notes", notes);

      return (
        <>
          <Flex position="relative">
            <Tooltip
              // closeDelay={300}
              // bg={backgroundLight}
              borderRadius="1rem"
              p="0.5rem 1rem"
              overflow="hidden"
              label={
                <Flex direction="column">
                  {notes && (
                    <Text fontSize="xs">
                      {/* {notes.text.length > 100
                        ? `${notes.text.slice(0, 100)}...`
                        : notes.text} */}

                      {notes.length > 100 ? `${notes.slice(0, 100)}...` : notes}
                    </Text>
                  )}
                </Flex>
              }
              placement="top"
              hasArrow
              isDisabled={!notes}
            >
              <IconButton
                bg={notes ? primary : backgroundLight}
                size="xs"
                borderRadius="10rem"
                onClick={() => console.log("clicked")}
                _hover={{
                  bg: notes ? primaryAlt : backgroundLight,
                }}
              >
                <Icon
                  as={notes ? TbMessage : TbMessage2Off}
                  w="initial"
                  h="0.875rem"
                  color={notes ? white : "#ddd"}
                  pointerEvents="none"
                />
              </IconButton>
            </Tooltip>
          </Flex>
        </>
      );
    },
  },

  {
    Header: () => (
      <Flex onClick={() => handleSortChange("coordinator")} h="100%">
        <Flex align="center" cursor="pointer">
          <Text mr={2}>{translate("treatmentPlan.columnCoordinator")}</Text>
        </Flex>
      </Flex>
    ),
    accessor: "coordinator",
    // width: 140,
    disableSortBy: true,
    Cell: ({ row }) => {
      const { coordinator } = row.original;

      const name =
        coordinator?.firstName && coordinator?.lastName
          ? `${coordinator.firstName[0]}. ${coordinator.lastName}`
          : undefined;

      const initials =
        name &&
        name
          .split(" ")
          .map((n) => n[0])
          .join("");

      return (
        <Flex align="center" justify="flex-start" textAlign="left">
          <Flex
            align="center"
            justify="center"
            bg={color8}
            h="1.35rem"
            w="1.35rem"
            borderRadius="50%"
            mr="0.25rem"
          >
            <Text fontSize="xs" color={white} fontWeight="500">
              {initials}
            </Text>
          </Flex>
          <Text
            fontSize="0.8rem"
            color={textPrimary}
            fontWeight="500"
            _groupHover={{
              color: white,
            }}
            maxW="90px"
            isTruncated
          >
            {name}
          </Text>
        </Flex>
      );
    },
  },

  {
    Header: () => (
      <Flex onClick={() => handleSortChange("status")} w="100%" h="100%">
        <Flex align="center" cursor="pointer" justify="flex-end" w="100%">
          <Text mr={2}>{translate("patient.columnStatus")}</Text>
        </Flex>
      </Flex>
    ),
    accessor: "status",
    disableSortBy: true,
    preventRowClick: true,
    Cell: ({ row }) => {
      const {
        status,
        _id,
        patient,
        treatmentPlanID,
        availablePublic,
        publicEmailDate,
        emailSentBy,
        emailSentByName,
        emailSentByEmail,
      } = row.original;

      const handleUpdateStatus = (newStatus) => {
        // dispatch(updateSpeakerStatus(_id, newStatus));
      };

      // console.log("patient", patient);

      return (
        <HStack gap={0} justify="flex-end">
          {availablePublic && publicEmailDate !== null ? (
            <Tooltip
              p="0.5rem 1rem"
              borderRadius="1rem"
              placement="top"
              label={
                <Flex direction="column">
                  <Flex align="center" gap="0.25rem">
                    <Text
                      fontSize="xs"
                      color={whiteAlt}
                      fontWeight="500"
                      textAlign="center"
                      whiteSpace="nowrap"
                    >
                      {translate("treatmentPlan.emailSentToPatient")}:
                    </Text>
                    <Text
                      fontSize="xs"
                      color={white}
                      fontWeight="500"
                      textAlign="center"
                      whiteSpace="nowrap"
                      textTransform="capitalize"
                    >
                      {dayjs(publicEmailDate).format("YYYY MMMM D, HH:mm")}
                    </Text>
                  </Flex>
                  <Flex align="center" gap="0.25rem">
                    <Text
                      fontSize="xs"
                      color={whiteAlt}
                      fontWeight="500"
                      textAlign="center"
                      whiteSpace="nowrap"
                    >
                      {translate("treatmentPlan.emailSentToPatientBy")}:
                    </Text>
                    <Text
                      fontSize="xs"
                      color={white}
                      fontWeight="500"
                      textAlign="center"
                      whiteSpace="nowrap"
                      isTruncated
                      maxW="220px"
                    >
                      {/* {emailSentBy &&
                        emailSentBy.lastName &&
                        emailSentBy.lastName}{" "}
                      {emailSentBy && emailSentBy.firstName} */}
                      {emailSentByName}, {emailSentByEmail}
                    </Text>
                  </Flex>
                </Flex>
              }
            >
              <Flex
                bg={color8}
                align="center"
                justify="center"
                w="1.25rem"
                h="1.25rem"
                borderRadius="10rem"
                mr="1rem"
                flexShrink={0}
              >
                <Icon as={FaEnvelope} fontSize="0.6rem" color={white} />
              </Flex>
            </Tooltip>
          ) : (
            <Flex
              bg={backgroundLight}
              align="center"
              justify="center"
              w="1.25rem"
              h="1.25rem"
              borderRadius="10rem"
              mr="1rem"
              flexShrink={0}
            >
              <Icon as={TbMailX} fontSize="0.8rem" color={textSecondary} />
            </Flex>
          )}
          {/* <StatusButtonNew
            isUnclickable
            currentStatus={status}
            onUpdateStatus={handleUpdateStatus}
            options="treatment"
            isRounded
          /> */}
          <StatusButtonNewLiteTp
            currentStatus={status}
            onUpdateStatus={handleUpdateStatus}
            options="treatment"
            isRounded
          />

          <MoreButtonLite
            id={_id}
            viewHandler={() =>
              navigate(`/${slug}/patients/${patient && patient._id}`)
            }
            viewLabel={translate("treatmentPlan.viewPatient")}
            viewId={patient && patient._id}
            editHandler={() => {
              setPlanId(_id);
              openEditSidePanel();
            }}
            editLabel={translate("treatmentPlan.editPlan")}
            editId={_id}
            deleteHandler={() => deleteTreatmentPlanHandler(_id)} // Set delete handler with specific patient ID
            setShowDeleteConfirmation={() => {
              setPlanId(_id); // Set the ID for deletion
              setTreatmentPlanID(treatmentPlanID);
              setPatientName(
                `${patient && patient.lastName} ${patient && patient.firstName}`
              );
              setShowDeleteConfirmation(true); // Open confirmation modal
            }}
            // deleteButtonDisabled={!isAdmin}
          />

          {/* <MoreButton
            id={_id}
            deleteHandler={deleteTreatmentPlanHandler}
            viewHandler={openPatient}
            viewLabel={translate("treatmentPlan.viewPatient")}
            viewId={patient && patient._id}
            editHandler={(e) => {
              e.stopPropagation();
              openSidePanelModalHandler(_id);
            }}
            editLabel={translate("treatmentPlan.edit")}
            deleteButtonDisabled={!isAdmin}
          /> */}
        </HStack>
      );
    },
  },
];
